import type { z } from 'zod';

import { ApiError } from './errors';

/*
 * Runtime edge-parse for the untyped BFF/Identity shims (finding D / Group 2).
 *
 * The hand-authored transports in `lib/api/domains/*` for routes NOT yet in
 * `openapi.json` (ADR 0009) return `data as T` with no runtime check. A shape
 * DRIFT (a renamed/missing field) or an unexpected empty body (a 204 where an
 * object/array is required) would otherwise flow untyped into a table cell and
 * surface as a raw `TypeError` in render. This helper parses the response at
 * the transport boundary and, on a parse failure or an empty body, throws an
 * `ApiError` that the normal error path handles — never a raw throw in render.
 *
 * The thrown `ApiError` carries a 502-class status so the query layer's
 * `shouldRetry` treats a transient drift as RETRYABLE (like any other 5xx) and
 * the consuming surface shows its error state. It is UI-free (lib/api) and used
 * only by the untyped shims; typed routes keep their generated-type contract.
 */

const SHIM_DRIFT_STATUS = 502;
const SHIM_DRIFT_CODE = 'shim_response_drift';

/**
 * Parse an untyped shim response against `schema`, returning the typed value or
 * throwing a handled, retryable `ApiError` on drift / empty body.
 *
 * @param schema the Zod schema for the expected response shape
 * @param data   the raw `data` returned by the untyped client
 * @param url    the request path, for the error's `url` (correlation only)
 */
export function parseShimResponse<T>(
  schema: z.ZodType<T>,
  data: unknown,
  url: string,
): T {
  // An absent body where a value is required (e.g. a 204 on a read) is a drift.
  if (data === undefined || data === null) {
    throw shimDriftError(url, 'empty response body');
  }
  const result = schema.safeParse(data);
  if (!result.success) {
    throw shimDriftError(url, result.error.message);
  }
  return result.data;
}

function shimDriftError(url: string, detail: string): ApiError {
  return new ApiError({
    status: SHIM_DRIFT_STATUS,
    code: SHIM_DRIFT_CODE,
    // Generic, cause-free message surfaced to the UI; the detail is for logs
    // only and carries no PII (a Zod path, never a value).
    message: 'The server returned an unexpected response.',
    url,
    body: { detail },
  });
}
